/**
 * One diary entry, and the three things that can be done to it.
 *
 * ===========================================================================
 * RESCHEDULING A HEARING IS NOT THE SAME ACT AS MOVING A MEETING
 * ===========================================================================
 * `POST /calendar-events/{id}/reschedule/` is for HEARINGS ONLY, and the server
 * refuses anything else with a 409 `not_a_hearing`. It emits the client-facing
 * rescheduling event and writes the matter's timeline — right for a hearing, and wrong
 * for an internal review, because a client should not be told their hearing moved
 * because the firm shifted a supervision meeting.
 *
 * Anything else changes time through a plain PATCH, which notifies nobody. This drawer
 * therefore branches on the event type and SAYS WHICH IT IS DOING, rather than offering
 * one "reschedule" button whose consequences silently differ.
 *
 * The PATCH carries `If-Match` from the record's `version`. A stale write comes back
 * 409 rather than quietly overwriting a colleague's change to the same listing.
 * ===========================================================================
 *
 * `outcome` is INTERNAL. The client is told the hearing took place; what was said in
 * court is for their lawyer to explain, not for a status line to summarise. It renders
 * inside a container labelled in words.
 *
 * There is no delete. No role in the catalogue holds `calendar.delete` at any scope,
 * and cancelling is better modelling anyway: attendees were told it was happening, and
 * an entry that simply vanishes leaves nobody able to say whether it moved, was
 * dropped, or was never real.
 */

import {
  ConflictError,
  STAFF,
  qk,
  useApi,
  useApiMutation,
  type CalendarEvent,
  type CaseListItem,
  type FirmMember,
} from '@legalcrm/api';
import {
  Badge,
  Button,
  Drawer,
  Input,
  Modal,
  Textarea,
  useToast,
} from '@legalcrm/ui';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Can } from '@/auth/permissions';
import { formatDateTime, humanise } from '@/lib/format';

/** `datetime-local` wants local wall-clock, and the record holds a UTC instant. */
function toLocalInput(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number) => String(value).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

function refusal(error: unknown, fallback: string): string {
  return error instanceof ConflictError ? error.detail : fallback;
}

function invalidations() {
  return [qk.all('calendar-events'), qk.all('cases')];
}

export interface EventDrawerProps {
  event: CalendarEvent;
  onClose: () => void;
  cases: CaseListItem[];
  members: FirmMember[];
}

type OpenDialog = 'reschedule' | 'complete' | 'cancel' | null;

export function EventDrawer({ event, onClose, cases, members }: EventDrawerProps) {
  const [dialog, setDialog] = useState<OpenDialog>(null);

  const isHearing = event.event_type === 'hearing';
  const isOver = event.status === 'completed' || event.status === 'cancelled';
  const matter = cases.find((record) => record.id === event.case);

  const attendeeName = (userId: string | null) => {
    if (!userId) return 'A client contact';
    const member = members.find((candidate) => candidate.user === userId);
    return member?.full_name || member?.email || 'A firm member';
  };

  return (
    <>
      <Drawer
        open
        onClose={onClose}
        title={event.title}
        description={humanise(event.event_type)}
        size="lg"
        footer={
          !isOver ? (
            // `calendar.edit` — reschedule, complete and cancel all map onto it in
            // `CalendarEventViewSet.kernel_action`. `_actions` is the accurate answer
            // for this record and wins over the bare capability.
            <Can actions={event._actions} action="edit">
              <Button variant="ghost" onClick={() => setDialog('cancel')}>
                Call it off
              </Button>
              {isHearing && (
                <Button variant="secondary" onClick={() => setDialog('complete')}>
                  Record the outcome
                </Button>
              )}
              <Button variant="primary" onClick={() => setDialog('reschedule')}>
                {isHearing ? 'Reschedule' : 'Change the time'}
              </Button>
            </Can>
          ) : null
        }
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-2">
            <Badge tone={event.status === 'cancelled' ? 'neutral' : 'info'} dot>
              {humanise(event.status)}
            </Badge>
            {/* Never colour alone. The words carry it. */}
            <Badge tone={event.visibility === 'internal' ? 'internal' : 'client-facing'}>
              {event.visibility === 'internal'
                ? 'Internal only'
                : 'The client can see this'}
            </Badge>
          </div>

          <dl className="flex flex-col gap-3 border-t pt-4">
            <Row label="Starts">{formatDateTime(event.starts_at)}</Row>
            {event.ends_at && <Row label="Ends">{formatDateTime(event.ends_at)}</Row>}
            {event.all_day && <Row label="Duration">All day</Row>}
            {event.timezone && <Row label="Entered in">{event.timezone}</Row>}
            {event.location && <Row label="Location">{event.location}</Row>}
            {event.court_name && <Row label="Court">{event.court_name}</Row>}
            {event.listing_reference && (
              <Row label="Listing">{event.listing_reference}</Row>
            )}
            <Row label="Matter">
              {event.case ? (
                <Link to={`/cases/${event.case}`} className="text-primary hover:underline">
                  {matter ? `${matter.case_number} — ${matter.title}` : 'Open matter'}
                </Link>
              ) : (
                <span className="text-ink-subtle">Not attached to a matter</span>
              )}
            </Row>
          </dl>

          <div className="border-t pt-4">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-subtle">
              Attending
            </p>
            {event.attendees.length === 0 ? (
              <p className="text-sm text-ink-subtle">Nobody has been recorded as attending.</p>
            ) : (
              <ul className="flex flex-col gap-1 text-base text-ink">
                {event.attendees.map((attendee) => (
                  <li key={attendee.id}>{attendeeName(attendee.user)}</li>
                ))}
              </ul>
            )}
          </div>

          {event.outcome && (
            /*
              Labelled internal in words as well as colour. The client is told the
              hearing happened; what was said in it is not a status line.
            */
            <div className="rounded-lg border border-internal/40 bg-internal-subtle/40 p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-internal">
                Internal only — never shown to the client
              </p>
              <p className="whitespace-pre-line text-base text-ink">{event.outcome}</p>
            </div>
          )}

          {event.next_event && (
            <p className="text-sm text-ink-muted">
              A follow-on listing was created when this hearing was completed.
            </p>
          )}
        </div>
      </Drawer>

      {dialog === 'reschedule' && (
        <RescheduleDialog event={event} onClose={() => setDialog(null)} onDone={onClose} />
      )}
      {dialog === 'complete' && (
        <CompleteHearingDialog event={event} onClose={() => setDialog(null)} onDone={onClose} />
      )}
      {dialog === 'cancel' && (
        <CancelEventDialog event={event} onClose={() => setDialog(null)} onDone={onClose} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Reschedule / move
// ---------------------------------------------------------------------------

function RescheduleDialog({
  event,
  onClose,
  onDone,
}: {
  event: CalendarEvent;
  onClose: () => void;
  onDone: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();
  const isHearing = event.event_type === 'hearing';
  const [startsAt, setStartsAt] = useState(toLocalInput(event.starts_at));

  const move = useApiMutation<CalendarEvent, string>(
    {
      mutationFn: (local) => {
        const starts_at = new Date(local).toISOString();
        return isHearing
          ? api.post<CalendarEvent>(STAFF.calendarEventReschedule(event.id), { starts_at })
          : // Not a hearing: a plain edit, notifying nobody. `If-Match` makes a stale
            // write a 409 rather than a silent overwrite of somebody else's change.
            api.patch<CalendarEvent>(
              STAFF.calendarEvent(event.id),
              { starts_at },
              { ifMatch: event.version },
            );
      },
      invalidates: invalidations(),
    },
    {
      onSuccess: () => {
        toast({
          title: isHearing ? 'Hearing rescheduled' : 'Time changed',
          description: isHearing
            ? 'The client has been told and the matter’s timeline records the change.'
            : 'An internal entry. Nobody has been notified.',
          tone: 'success',
        });
        onClose();
        onDone();
      },
      onError: (error) =>
        toast({
          title: 'Could not change the time',
          description: refusal(error, 'The entry could not be moved. Try again.'),
          tone: 'warning',
        }),
    },
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={isHearing ? 'Reschedule this hearing' : 'Change the time'}
      description={event.title}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={move.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!startsAt}
            loading={move.isPending}
            onClick={() => move.mutate(startsAt)}
          >
            {isHearing ? 'Reschedule' : 'Save the new time'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* The consequence is stated before the control, because it is the reason to
            hesitate — and it differs entirely between the two cases. */}
        {isHearing ? (
          <div className="rounded border border-client-facing/40 bg-client-facing-subtle/40 px-3 py-2 text-sm text-ink">
            <strong className="font-semibold">The client will be told.</strong> Rescheduling
            a hearing emits the client-facing event and writes the change onto the
            matter&apos;s timeline.
          </div>
        ) : (
          <div className="rounded border bg-surface-sunken px-3 py-2 text-sm text-ink-muted">
            This is an internal entry, so moving it notifies nobody and does not touch the
            matter&apos;s timeline.
          </div>
        )}

        <p className="text-sm text-ink-muted">
          Currently {formatDateTime(event.starts_at)}.
        </p>

        <Input
          label="New start"
          type="datetime-local"
          required
          value={startsAt}
          onChange={(changed) => setStartsAt(changed.target.value)}
        />
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Complete a hearing
// ---------------------------------------------------------------------------

function CompleteHearingDialog({
  event,
  onClose,
  onDone,
}: {
  event: CalendarEvent;
  onClose: () => void;
  onDone: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();
  const [outcome, setOutcome] = useState('');
  const [nextHearingAt, setNextHearingAt] = useState('');

  const complete = useApiMutation<
    CalendarEvent,
    { outcome: string; next_hearing_at: string | null }
  >(
    {
      mutationFn: (body) =>
        api.post<CalendarEvent>(STAFF.calendarEventComplete(event.id), body),
      invalidates: invalidations(),
    },
    {
      onSuccess: (updated) => {
        toast({
          title: 'Outcome recorded',
          description: updated.next_event
            ? 'The next listing has been created and chained to this one.'
            : 'The client is told the hearing took place, not what was said in it.',
          tone: 'success',
        });
        onClose();
        onDone();
      },
      onError: (error) =>
        toast({
          title: 'Could not record the outcome',
          description: refusal(error, 'It could not be recorded. Try again.'),
          tone: 'warning',
        }),
    },
  );

  return (
    <Modal
      open
      onClose={onClose}
      title="Record what happened"
      description={event.title}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={complete.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!outcome.trim()}
            loading={complete.isPending}
            onClick={() =>
              complete.mutate({
                outcome: outcome.trim(),
                next_hearing_at: nextHearingAt
                  ? new Date(nextHearingAt).toISOString()
                  : null,
              })
            }
          >
            Record it
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="rounded-lg border border-internal/40 bg-internal-subtle/40 p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-internal">
            Internal only — never shown to the client
          </p>
          <Textarea
            label="Outcome"
            hint="The client is told the hearing took place. What was said in court is for their lawyer to explain, not for a status line to summarise."
            rows={4}
            required
            value={outcome}
            onChange={(changed) => setOutcome(changed.target.value)}
          />
        </div>

        <Input
          label="Next listing"
          type="datetime-local"
          hint="Optional. Creates a follow-on hearing, chained to this one, and tells the client about it."
          value={nextHearingAt}
          onChange={(changed) => setNextHearingAt(changed.target.value)}
        />
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Cancel
// ---------------------------------------------------------------------------

function CancelEventDialog({
  event,
  onClose,
  onDone,
}: {
  event: CalendarEvent;
  onClose: () => void;
  onDone: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();
  const [reason, setReason] = useState('');

  const cancel = useApiMutation<CalendarEvent, { reason: string }>(
    {
      mutationFn: (body) => api.post<CalendarEvent>(STAFF.calendarEventCancel(event.id), body),
      invalidates: invalidations(),
    },
    {
      onSuccess: () => {
        toast({ title: 'Entry cancelled', tone: 'success' });
        onClose();
        onDone();
      },
      onError: (error) =>
        toast({
          title: 'Could not cancel',
          description: refusal(error, 'It could not be cancelled. Try again.'),
          tone: 'warning',
        }),
    },
  );

  return (
    <Modal
      open
      onClose={onClose}
      title="Call this off"
      description={event.title}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={cancel.isPending}>
            Keep it
          </Button>
          <Button
            variant="danger"
            loading={cancel.isPending}
            onClick={() => cancel.mutate({ reason: reason.trim() })}
          >
            Cancel the entry
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-muted">
          The entry stays on the record, marked cancelled. It is not removed: attendees
          were told it was happening, and an entry that simply disappears leaves nobody
          able to say whether it moved, was dropped, or was never real.
        </p>
        <Textarea
          label="Reason"
          hint="Optional, and recorded in the audit trail rather than shown to the client."
          rows={3}
          value={reason}
          onChange={(changed) => setReason(changed.target.value)}
        />
      </div>
    </Modal>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs font-medium uppercase tracking-wide text-ink-subtle">{label}</dt>
      <dd className="text-right text-base text-ink">{children}</dd>
    </div>
  );
}
